import { useRef, useState, useMemo, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Plus, Search, Trash2 } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

type TabValue = 'all' | 'trash'

type SurveyQuestionDraft = {
  question_text: string
  question_type: 'rating' | 'text'
  sort_order: number
  required: boolean
}

const basePath = '/service-catalog/customer-satisfaction'

const emptyQuestion = (): SurveyQuestionDraft => ({
  question_text: '',
  question_type: 'rating',
  sort_order: 0,
  required: true,
})

const resolveSurveyId = (rowOrId: unknown): number | null => {
  if (rowOrId != null && typeof rowOrId === 'object') {
    const id = (rowOrId as { id?: unknown }).id
    if (id == null || id === '') return null
    const num = Number(id)
    return Number.isFinite(num) ? num : null
  }
  const num = Number(rowOrId)
  return Number.isFinite(num) ? num : null
}

export default function CustomerSatisfactionPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess } = useToastNotification()
  const formModal = useDisclosure()
  const access = useAccessCheck(basePath)

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState({
    name: '',
    description: '',
    active: true,
    questions: [emptyQuestion()] as SurveyQuestionDraft[],
  })

  const setSubSub = useCallback((all: number, trashed: number) => {
    setTotalRows(all)
    setTotalTrash(trashed)
  }, [])

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      displayInModal: true,
      dataFields: {
        name: { name: 'Survey Name', withSort: true, maxWidth: '220px' },
        active: {
          name: 'Active',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Yes', false: 'No' },
        },
        description: { name: 'Description', maxWidth: '280px' },
        updated_at: { name: 'Updated', withSort: true, maxWidth: '160px' },
      },
      dataSource: activeTab === 'trash' ? `${basePath}/archived` : basePath,
      primaryKey: 'id',
      softDelete: true,
      noRecordText: 'No CSAT surveys found',
    }),
    [activeTab]
  )

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      active: true,
      questions: [emptyQuestion()],
    })
    setErrors({})
    setEditingId(null)
  }

  const openCreate = () => {
    resetForm()
    formModal.onOpen()
  }

  const openEdit = async (rowOrId: unknown) => {
    const id = resolveSurveyId(rowOrId)
    if (!id) {
      showError(new Error('Invalid survey id'))
      return
    }

    setEditingId(id)
    setErrors({})
    try {
      const { data } = await apiClient.get(`${basePath}/${id}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        description: r.description ?? '',
        active: r.active === true,
        questions:
          (r.questions ?? []).length > 0
            ? r.questions.map((q: SurveyQuestionDraft, i: number) => ({
                question_text: q.question_text ?? '',
                question_type: q.question_type === 'text' ? 'text' : 'rating',
                sort_order: q.sort_order ?? i,
                required: q.required !== false,
              }))
            : [emptyQuestion()],
      })
      formModal.onOpen()
    } catch (e) {
      showError(e as Error)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setErrors({})
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        active: form.active,
        questions: form.questions
          .filter((q) => q.question_text.trim() !== '')
          .map((q, i) => ({
            question_text: q.question_text.trim(),
            question_type: q.question_type,
            sort_order: i,
            required: q.required,
          })),
      }

      if (editingId) {
        await apiClient.put(`${basePath}/${editingId}`, payload)
        showSuccess('Success', 'Survey updated successfully')
      } else {
        await apiClient.post(basePath, payload)
        showSuccess('Success', 'Survey created successfully')
      }

      formModal.onClose()
      resetForm()
      tableRef.current?.reload()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors) setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  const updateQuestion = (index: number, patch: Partial<SurveyQuestionDraft>) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }))
  }

  const addQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questions: [...prev.questions, { ...emptyQuestion(), sort_order: prev.questions.length }],
    }))
  }

  const removeQuestion = (index: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }))
  }

  return (
    <Box p={4}>
      <VStack align="stretch" spacing={4}>
        <Card>
          <CardHeader>
            <Heading size="md">Customer Satisfaction Surveys</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Build and manage CSAT surveys sent when tickets are resolved.
            </Text>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={3}>
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={(i) => setActiveTab(i === 0 ? 'all' : 'trash')}>
                  <TabList>
                    <Tab>
                      All <Badge ml={2}>{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <HStack>
                  <InputGroup maxW="280px">
                    <Input
                      ref={searchRef}
                      placeholder="Search surveys…"
                      size="sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const q = searchRef.current?.value ?? ''
                          setSearchQuery(q)
                          tableRef.current?.applyFilter('search', q)
                        }
                      }}
                    />
                  </InputGroup>
                  <IconButton
                    aria-label="Search"
                    icon={<Search size={16} />}
                    size="sm"
                    onClick={() => {
                      const q = searchRef.current?.value ?? ''
                      setSearchQuery(q)
                      tableRef.current?.applyFilter('search', q)
                    }}
                  />
                  <ActionButtons
                    pathname={basePath}
                    onCreate={openCreate}
                    createLabel="New Survey"
                  />
                </HStack>
              </HStack>

              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                setSubSub={setSubSub}
                access={permissions}
                onOpenModal={(rowOrId) => void openEdit(rowOrId)}
              />
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      <Modal
        isOpen={formModal.isOpen}
        onClose={() => {
          formModal.onClose()
          resetForm()
        }}
        size="2xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? 'Edit Survey' : 'New Survey'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired isInvalid={!!errors.name?.length}>
                <FormLabel>Survey Name</FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Post-resolution CSAT"
                />
                <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </FormControl>

              <Checkbox
                isChecked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              >
                Active (used for new invitations)
              </Checkbox>

              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="700">Questions</Text>
                  <Button size="xs" leftIcon={<Plus size={14} />} onClick={addQuestion}>
                    Add Question
                  </Button>
                </HStack>
                <VStack align="stretch" spacing={3}>
                  {form.questions.map((q, index) => (
                    <Box key={index} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                      <HStack align="start" spacing={3}>
                        <VStack align="stretch" flex={1} spacing={2}>
                          <Input
                            value={q.question_text}
                            onChange={(e) => updateQuestion(index, { question_text: e.target.value })}
                            placeholder="Question text"
                          />
                          <Select
                            size="sm"
                            value={q.question_type}
                            onChange={(e) =>
                              updateQuestion(index, {
                                question_type: e.target.value as 'rating' | 'text',
                              })
                            }
                          >
                            <option value="rating">Rating (1–5)</option>
                            <option value="text">Free text</option>
                          </Select>
                          <Checkbox
                            isChecked={q.required}
                            onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                          >
                            Required
                          </Checkbox>
                        </VStack>
                        <IconButton
                          aria-label="Remove question"
                          icon={<Trash2 size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          isDisabled={form.questions.length <= 1}
                          onClick={() => removeQuestion(index)}
                        />
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                formModal.onClose()
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => void handleSave()} isLoading={saving}>
              Save Survey
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
